import { useEffect, useMemo, useState } from "react";
import { Check, Clipboard, Download, ExternalLink, Share2, X } from "lucide-react";
import { buildVentXhsShareBundle, buildXhsShareBundle, type XhsShareBundle } from "../services/xhsShare";
import type { ActionArtifact, DrawnCard, Reading, Scenario, VentRitualResult } from "../types";

interface Props {
  open: boolean;
  onClose: () => void;
  scenario: Scenario;
  cards: DrawnCard[];
  report?: Reading | null;
  artifact?: ActionArtifact | null;
  ventShare?: { target:string; behaviors:string[]; ritual:VentRitualResult; pinPoints?:Array<{x:number;y:number}> } | null;
}

export function XhsShareModal({ open, onClose, scenario, cards, report, artifact, ventShare }: Props) {
  const [bundle, setBundle] = useState<XhsShareBundle | null>(null);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [sharing, setSharing] = useState(false);
  const previews = useMemo(() => bundle?.files.map((file) => URL.createObjectURL(file)) ?? [], [bundle]);

  useEffect(() => () => previews.forEach(URL.revokeObjectURL), [previews]);
  useEffect(() => {
    if (!open || (!report && !ventShare)) return;
    let cancelled = false;
    const task = ventShare ? buildVentXhsShareBundle(ventShare) : buildXhsShareBundle({ scenario, cards, report:report!, artifact });
    void task
      .then((value) => { if (!cancelled) setBundle(value); })
      .catch((reason) => { if (!cancelled) setError(reason instanceof Error ? reason.message : "分享图生成失败"); });
    return () => { cancelled = true; };
  }, [open, scenario, cards, report, artifact, ventShare]);
  if (!open || (!report && !ventShare)) return null;

  async function copyCaption() {
    if (!bundle) return;
    try { await navigator.clipboard.writeText(bundle.caption); }
    catch {
      const area = document.createElement("textarea"); area.value = bundle.caption; area.style.cssText = "position:fixed;opacity:0";
      document.body.appendChild(area); area.select(); document.execCommand("copy"); area.remove();
    }
    setNotice("笔记文案已复制，到小红书后直接粘贴即可");
  }

  function saveAll() {
    if (!bundle) return;
    bundle.files.forEach((file, index) => window.setTimeout(() => {
      const url = URL.createObjectURL(file); const link = document.createElement("a");
      link.href = url; link.download = file.name; document.body.appendChild(link); link.click(); link.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 1_000);
    }, index * 250));
    setNotice(`正在保存 ${bundle.files.length} 张笔记图片`);
  }

  async function shareToXhs() {
    if (!bundle) return;
    setSharing(true); setNotice("");
    try {
      await copyCaption();
      const data: ShareData = { title:bundle.title, text:bundle.caption, files:bundle.files };
      if (navigator.share && navigator.canShare?.({ files:bundle.files })) {
        await navigator.share(data);
        setNotice("分享面板已打开：选择小红书并粘贴文案即可发布");
      } else {
        saveAll();
        setNotice("浏览器不支持图片直传：图片已保存、文案已复制，请打开小红书发布");
      }
    } catch (reason) {
      if (reason instanceof Error && reason.name !== "AbortError") { saveAll(); setNotice("直传未成功，已改为保存图片并复制文案"); }
    } finally { setSharing(false); }
  }

  async function openXhs() {
    await copyCaption(); saveAll();
    setNotice("图片正在保存、文案已复制，即将打开小红书发布页");
    window.setTimeout(() => { window.location.href = "xhsdiscover://post"; }, Math.max(900, (bundle?.files.length ?? 1) * 260));
  }

  return <div className="modal-backdrop xhs-backdrop" role="presentation" onMouseDown={onClose}>
    <section className="xhs-modal" role="dialog" aria-modal="true" aria-labelledby="xhs-title" onMouseDown={(event) => event.stopPropagation()}>
      <header className="xhs-modal-head"><div><p className="eyebrow">小红书图文笔记包</p><h2 id="xhs-title">预览并分享到小红书</h2></div><button className="icon-button" onClick={onClose} aria-label="关闭"><X /></button></header>
      <p className="muted">已自动生成 3:4 图片、标题、正文和话题。分享前不会包含你的原问题、私人吐槽或补充背景。</p>
      {!bundle && !error && <div className="share-loading"><span>✦</span><p>正在排版小红书笔记…</p></div>}
      {error && <p className="error">{error}</p>}
      {bundle && <>
        <div className="poster-track" aria-label="小红书笔记图片预览">{previews.map((src, index) => <figure key={src}><img src={src} alt={`小红书笔记第 ${index + 1} 张`} /><figcaption>{index + 1} / {previews.length}</figcaption></figure>)}</div>
        <div className="caption-preview"><div><b>笔记文案</b><span>{bundle.caption.length} 字</span></div><p>{bundle.caption}</p></div>
        {notice && <p className="share-notice"><Check size={16} />{notice}</p>}
        <div className="xhs-primary-actions"><button className="xhs-share" disabled={sharing} onClick={() => void shareToXhs()}><Share2 />{sharing ? "正在准备…" : "一键分享到小红书"}</button><button className="secondary" onClick={() => void openXhs()}><ExternalLink size={18} />保存并打开小红书</button></div>
        <div className="xhs-secondary-actions"><button onClick={saveAll}><Download size={16} />保存全部图片</button><button onClick={() => void copyCaption()}><Clipboard size={16} />复制笔记文案</button></div>
        <p className="fineprint">网页会调起手机系统分享面板；请选择“小红书”。出于账号安全，最后的编辑与发布需由你确认。</p>
      </>}
    </section>
  </div>;
}
